import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  InputAdornment,
  Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import Loader from '../General/Loader';
import BackButton from '../../components/BackButton';

const SellerSettlementView = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    fetchTransactions();
  }, [page, pageSize, statusFilter]);

  const fetchTransactions = async (mobileNumber = '') => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/seller-settlements`;
      if (mobileNumber) {
        url = `${API_BASE_URL}/seller-settlements/shop/${mobileNumber}`;
      }
      
      const params = new URLSearchParams({
        page: page + 1,
        limit: pageSize,
        ...(statusFilter && { txnStatus: statusFilter })
      });

      const response = await axios.get(`${url}?${params}`);
      
      if (response.data.data.transactions.length === 0) {
        showSnackbar('No transactions found', 'info');
      }
      
      setTransactions(response.data.data.transactions);
      setTotalCount(response.data.data.totalCount);
    } catch (error) {
      showSnackbar(
        'Error fetching transactions. Please try again.',
        'error'
      );
      setTransactions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneNumberChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(value);
  };

  const handleSearch = () => {
    if (phoneNumber.length === 10) {
      setPage(0);
      fetchTransactions(phoneNumber);
    }
  };

  const handleClearSearch = () => {
    setPhoneNumber('');
    setPage(0);
    fetchTransactions();
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const showSnackbar = (message, severity = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handlePrint = () => {
    if (transactions.length === 0) {
      showSnackbar('No data to print', 'warning');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Seller Settlement Transactions Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #333;
            }
            .filters {
              margin-bottom: 20px;
              padding: 10px;
              background-color: #f5f5f5;
              border-radius: 4px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 12px 8px;
              text-align: left;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 20px;
              text-align: right;
              font-weight: bold;
            }
            .status {
              padding: 4px 8px;
              border-radius: 4px;
              font-weight: bold;
            }
            .status-success {
              color: #2e7d32;
            }
            .status-failed {
              color: #d32f2f;
            }
            .status-pending {
              color: #ed6c02;
            }
            @media print {
              .header {
                position: fixed;
                top: 0;
                width: 100%;
              }
              .footer {
                position: fixed;
                bottom: 0;
                width: 100%;
              }
              table {
                page-break-inside: auto;
              }
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Seller Settlement Transactions Report</h1>
            <p>Generated on: ${new Date().toLocaleString('en-IN', {
              dateStyle: 'full',
              timeStyle: 'medium'
            })}</p>
          </div>
          
          <div class="filters">
            <strong>Filters:</strong>
            ${phoneNumber ? `<br>Phone Number: ${phoneNumber}` : ''}
            ${statusFilter ? `<br>Status: ${statusFilter}` : 'All Statuses'}
          </div>

          <table>
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Mobile</th>
                <th>Reference Number</th>
                <th>UTR</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Initiation Date</th>
                <th>Failure Reason</th>
              </tr>
            </thead>
            <tbody>
              ${transactions.map(transaction => `
                <tr>
                  <td>${transaction.businessName || 'N/A'}</td>
                  <td>${transaction.mobileNumber || 'N/A'}</td>
                  <td>${transaction.yppReferenceNumber || 'N/A'}</td>
                  <td>${transaction.utr || 'N/A'}</td>
                  <td>${formatCurrency(transaction.amount || 0)}</td>
                  <td>
                    <span class="status status-${transaction.txnStatus?.toLowerCase()}">
                      ${transaction.txnStatus || 'N/A'}
                    </span>
                  </td>
                  <td>${transaction.initiationDate ? 
                    new Date(transaction.initiationDate).toLocaleString('en-IN', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    }) : 'N/A'}
                  </td>
                  <td>${transaction.failureReason || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p>Total Transactions: ${transactions.length}<br>
            Total Amount: ${formatCurrency(
              transactions.reduce((sum, t) => sum + (t.amount || 0), 0)
            )}</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.onload = function() {
      printWindow.print();
      printWindow.onafterprint = function() {
        printWindow.close();
      };
    };
  };

  const columns = [
    {
      field: 'businessName',
      headerName: 'Business Name',
      width: 200,
      renderCell: (params) => (
        <Typography>{params.row?.businessName || 'N/A'}</Typography>
      )
    },
    {
      field: 'mobileNumber',
      headerName: 'Mobile',
      width: 130,
      renderCell: (params) => (
        <Typography>{params.row?.mobileNumber || 'N/A'}</Typography>
      )
    },
    {
      field: 'yppReferenceNumber',
      headerName: 'Reference Number',
      width: 180,
      renderCell: (params) => (
        <Typography>{params.row?.yppReferenceNumber || 'N/A'}</Typography>
      )
    },
    {
      field: 'utr',
      headerName: 'YPP Ref. No',
      width: 200,
      renderCell: (params) => (
        <Typography>{params.row?.yppReferenceNumber || 'N/A'}</Typography>
      )
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      renderCell: (params) => (
        <Typography>{formatCurrency(params.row?.amount || 0)}</Typography>
      )
    },
    {
      field: 'txnStatus',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Typography
          sx={{
            color: params.row?.txnStatus === 'SUCCESS' ? 'success.main' : 
                  params.row?.txnStatus === 'FAILED' ? 'error.main' : 'warning.main'
          }}
        >
          {params.row?.txnStatus || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'initiationDate',
      headerName: 'Initiation Date',
      width: 180,
      renderCell: (params) => (
        <Typography>
          {params.row?.initiationDate ? 
            new Date(params.row.initiationDate).toLocaleString('en-IN', {
              dateStyle: 'medium',
              timeStyle: 'short'
            }) : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'failureReason',
      headerName: 'Failure Reason',
      width: 200,
      renderCell: (params) => (
        <Typography>{params.row?.failureReason || 'N/A'}</Typography>
      )
    }
  ];

  return (
     <Box sx={{ position: 'relative', padding: 2 }}>
                  {/* Place BackButton outside the Grid */}
                  <BackButton />
                  <br/>  <br/>
    <Box sx={{ height: 'auto', width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Seller Settlement Transactions
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              helperText={`${phoneNumber.length}/10 digits required`}
              error={phoneNumber.length > 0 && phoneNumber.length < 10}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={phoneNumber.length !== 10}
              fullWidth
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              fullWidth
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <Select
                value={statusFilter}
                onChange={handleStatusChange}
                displayEmpty
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="SUCCESS">Success</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handlePrint}
              fullWidth
              startIcon={<PrintIcon />}
              disabled={transactions.length === 0}
            >
              Print
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Loader />
      ) : (
        <DataGrid
          rows={transactions}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          autoHeight
          getRowHeight={() => 'auto'}
          getRowId={(row) => row._id}
          sx={{
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              padding: '8px',
            },
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default SellerSettlementView; 